import { Icon } from '@chakra-ui/react'
import { MessageSquareText, Phone, Plus, Printer } from 'lucide-react'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useModalState } from '~/atoms/ModalState'
import { QuickActionButtonVariants } from '~/components/shared/componentStyles/QuickActionButton.style'
import { DropdownActionButton } from '~/components/shared/Dropdown/DropdownActionButton'
import { MenuItem, MenuListItem } from '~/components/shared/Dropdown/MenuItem'
import { useShowTexting } from '~/hooks/useCurrentPartner'
import { TranslationKeys } from '~/locales/i18next'
import { observe } from '~/services/observability/observe'
import {
  AnalyticsNamespace,
  FaxAnalyticsEvents,
  MessagingAnalyticsEvents,
  WebPhoneAnalyticsEvents,
} from '~/types/Analytics'

import { ActionsButtonStyleValues } from './componentStyles/ActionsButton.style'

const ActionsButton = () => {
  const { t } = useTranslation()
  const showTexting = useShowTexting()
  const {
    faxModal: [faxModalState, setFaxModalState],
    messagingModal: [, setMessagingModalState],
    callModal: [, setCallModalState],
  } = useModalState()

  const handleCreateNewChat = () => {
    setMessagingModalState({ open: true })
    observe.track(
      AnalyticsNamespace.MESSAGING,
      MessagingAnalyticsEvents.NewThreadModalOpened,
    )
  }

  const handleOpenCallModal = () => {
    setCallModalState({ open: true })
    observe.track(
      AnalyticsNamespace.WEB_PHONE,
      WebPhoneAnalyticsEvents.NewCallModalOpened,
    )
  }

  const handleOpenNewFax = () => {
    setFaxModalState({
      ...faxModalState,
      open: true,
      number: '',
    })
    observe.track(
      AnalyticsNamespace.FAXING,
      FaxAnalyticsEvents.NewFaxModalOpened,
    )
  }

  const getMenuItem = (
    primaryText: string,
    leftIcon: React.ComponentType<any>,
    onClick: () => void,
  ) => {
    const IconComp = leftIcon
    return {
      value: primaryText,
      content: (
        <MenuListItem
          primaryText={primaryText}
          leftContent={
            <Icon color="fg.default">
              <IconComp size={16} />
            </Icon>
          }
        />
      ),
      onSelect: onClick,
    } as MenuItem<string>
  }

  const menuItemData = useMemo(() => {
    return [
      ...(showTexting
        ? [
            {
              onClick: () => handleCreateNewChat(),
              icon: MessageSquareText,
              text: 'actionsButton.newThread',
            },
          ]
        : []),
      {
        onClick: () => handleOpenCallModal(),
        icon: Phone,
        text: 'actionsButton.newCall',
      },
      {
        onClick: () => handleOpenNewFax(),
        icon: Printer,
        text: 'actionsButton.newFax',
      },
    ]
  }, [showTexting])

  const menuItems = useMemo(
    () =>
      menuItemData.map((item) =>
        getMenuItem(t(item.text as TranslationKeys), item.icon, item.onClick),
      ),
    [menuItemData, t],
  )

  return (
    <DropdownActionButton
      variant={QuickActionButtonVariants.Default_300}
      tooltipText={t('actionsButton.tooltipNewAction')}
      icon={Plus}
      items={menuItems}
      menuOptions={{ width: ActionsButtonStyleValues.width }}
    />
  )
}

export { ActionsButton }
